import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { NoteTag } from '../types';
import type { CaseTag } from '../notesApi';

function parseTagNames(input: string): string[] {
  return [
    ...new Set(
      input
        .split('@')
        .map((s) => s.replace(/[\s,]+$/, ''))
        .filter(Boolean),
    ),
  ];
}

type TagInputProps = {
  selectedTags: NoteTag[];
  availableTags: CaseTag[];
  onChange: (tags: NoteTag[]) => void;
  onCreateTag?: (name: string) => Promise<NoteTag | null>;
  readOnly?: boolean;
};

const TagInput = React.forwardRef<HTMLInputElement, TagInputProps>(
  ({ selectedTags, availableTags, onChange, onCreateTag, readOnly = false }, ref) => {
    const [inputValue, setInputValue] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isCreating, setIsCreating] = useState(false);
    const innerRef = useRef<HTMLInputElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const selectedTagsRef = useRef(selectedTags);
    selectedTagsRef.current = selectedTags;

    const assignRef = useCallback(
      (el: HTMLInputElement | null) => {
        innerRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
      },
      [ref],
    );

    const selectedIds = useMemo(() => new Set(selectedTags.map((t) => t.id)), [selectedTags]);

    const { isAtMode, filterText, allParsedNames } = useMemo(() => {
      if (!inputValue.startsWith('@')) {
        return { isAtMode: false, filterText: '', allParsedNames: [] as string[] };
      }
      const allNames = parseTagNames(inputValue);
      const lastAtIdx = inputValue.lastIndexOf('@');
      const activeSegment = inputValue.slice(lastAtIdx + 1).replace(/[\s,]+$/, '');
      return { isAtMode: true, filterText: activeSegment, allParsedNames: allNames };
    }, [inputValue]);

    const isMultiTag = allParsedNames.length > 1;

    const newTagNames = useMemo(() => {
      return allParsedNames.filter(
        (name) => !availableTags.some((t) => t.name.toLowerCase() === name.toLowerCase()),
      );
    }, [allParsedNames, availableTags]);

    const filteredTags = useMemo(() => {
      if (!isAtMode) return [];
      const lower = filterText.toLowerCase();
      return availableTags
        .filter((t) => !selectedIds.has(t.id) && t.name.toLowerCase().includes(lower))
        .sort((a, b) => a.name.localeCompare(b.name));
    }, [availableTags, selectedIds, filterText, isAtMode]);

    useEffect(() => {
      setActiveIndex(0);
    }, [filteredTags.length]);

    useEffect(() => {
      setShowDropdown(isAtMode);
    }, [isAtMode]);

    // Scroll active item into view
    useEffect(() => {
      if (!showDropdown || !dropdownRef.current) return;
      const items = dropdownRef.current.querySelectorAll('.real-time__tag-dropdown-item');
      const active = items[activeIndex] as HTMLElement | undefined;
      if (active) active.scrollIntoView({ block: 'nearest' });
    }, [activeIndex, showDropdown]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      // Only allow typing after '@'. Accept '@' itself, or text that starts with '@'.
      // Reject any other input when the field is empty (force user to type '@' first).
      if (val === '' || val === '@' || val.startsWith('@')) {
        setInputValue(val);
      }
      // Ignore non-'@' input on an empty field
    }, []);

    const selectTag = useCallback(
      (tag: CaseTag) => {
        onChange([...selectedTags, { id: tag.id, name: tag.name }]);
        setInputValue('');
        setShowDropdown(false);
        innerRef.current?.focus();
      },
      [selectedTags, onChange],
    );

    const removeTag = useCallback(
      (tagId: string) => {
        onChange(selectedTags.filter((t) => t.id !== tagId));
      },
      [selectedTags, onChange],
    );

    const handleCreateTags = useCallback(async () => {
      if (!onCreateTag) return;
      const names =
        allParsedNames.length > 0 ? allParsedNames : filterText.trim() ? [filterText.trim()] : [];
      if (names.length === 0) return;

      setIsCreating(true);
      const tagsToAdd: NoteTag[] = [];

      // Separate existing tags from new ones
      const toCreate: string[] = [];
      for (const name of names) {
        const existing = availableTags.find((t) => t.name.toLowerCase() === name.toLowerCase());
        if (existing && !selectedIds.has(existing.id)) {
          tagsToAdd.push({ id: existing.id, name: existing.name });
        } else if (!existing) {
          toCreate.push(name);
        }
      }

      // Create new tags in parallel
      if (toCreate.length > 0) {
        const results = await Promise.allSettled(toCreate.map((name) => onCreateTag(name)));
        for (const result of results) {
          if (result.status === 'fulfilled' && result.value) {
            tagsToAdd.push(result.value);
          }
        }
      }

      setIsCreating(false);
      if (tagsToAdd.length > 0) {
        onChange([...selectedTagsRef.current, ...tagsToAdd]);
        setInputValue('');
        setShowDropdown(false);
        innerRef.current?.focus();
      }
    }, [onCreateTag, allParsedNames, filterText, availableTags, selectedIds, onChange]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && inputValue === '' && selectedTags.length > 0) {
          removeTag(selectedTags[selectedTags.length - 1].id);
          return;
        }

        if (showDropdown) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            setActiveIndex((prev) => (prev < filteredTags.length - 1 ? prev + 1 : prev));
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            e.stopPropagation();
            setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
            return;
          }
          if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Enter' && isMultiTag && onCreateTag && !isCreating) {
              handleCreateTags();
            } else if (filteredTags[activeIndex]) {
              selectTag(filteredTags[activeIndex]);
            } else if (
              e.key === 'Enter' &&
              filteredTags.length === 0 &&
              filterText.trim() &&
              onCreateTag &&
              !isCreating
            ) {
              handleCreateTags();
            }
            return;
          }
          if (e.key === 'Escape') {
            e.stopPropagation();
            setShowDropdown(false);
            setInputValue('');
            return;
          }
        }
      },
      [
        showDropdown,
        filteredTags,
        activeIndex,
        selectTag,
        filterText,
        onCreateTag,
        isCreating,
        isMultiTag,
        handleCreateTags,
        inputValue,
        selectedTags,
        removeTag,
      ],
    );

    if (readOnly) {
      return (
        <div className="real-time__tag-input real-time__tag-input--readonly">
          {selectedTags.map((tag) => (
            <span key={tag.id} className="real-time__tag-chip">
              {tag.name}
            </span>
          ))}
          {selectedTags.length === 0 && <span className="real-time__tag-input-empty">No tags</span>}
        </div>
      );
    }

    return (
      <div className="real-time__tag-input">
        {selectedTags.map((tag) => (
          <span key={tag.id} className="real-time__tag-chip">
            {tag.name}
            <button
              type="button"
              className="real-time__tag-chip-remove"
              onClick={() => removeTag(tag.id)}
            >
              <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
                <path
                  d="M4 4l6 6M10 4l-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </span>
        ))}
        <div className="real-time__tag-input-wrap">
          <input
            ref={assignRef}
            className="real-time__tag-input-field"
            type="text"
            placeholder="Type @ to search tags"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          {showDropdown && (
            <div className="real-time__tag-dropdown" ref={dropdownRef}>
              {filteredTags.map((tag, idx) => (
                <button
                  key={tag.id}
                  type="button"
                  className={`real-time__tag-dropdown-item${idx === activeIndex ? ' is-active' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectTag(tag);
                  }}
                >
                  {tag.name}
                </button>
              ))}
              {isMultiTag && onCreateTag && (
                <div className="real-time__tag-dropdown-empty-row">
                  <span className="real-time__tag-dropdown-empty">
                    {allParsedNames.length} tags
                    {newTagNames.length > 0 ? ` (${newTagNames.length} new)` : ''}
                  </span>
                  <button
                    type="button"
                    className="real-time__tag-create-btn"
                    disabled={isCreating}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleCreateTags();
                    }}
                  >
                    {isCreating
                      ? 'Creating...'
                      : newTagNames.length > 0
                        ? `Create ${newTagNames.length} Tag${newTagNames.length > 1 ? 's' : ''}`
                        : 'Add Tags'}
                  </button>
                </div>
              )}
              {!isMultiTag && filteredTags.length === 0 && (
                <div className="real-time__tag-dropdown-empty-row">
                  <span className="real-time__tag-dropdown-empty">No tags found</span>
                  {filterText.trim() && onCreateTag && (
                    <button
                      type="button"
                      className="real-time__tag-create-btn"
                      disabled={isCreating}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleCreateTags();
                      }}
                    >
                      {isCreating ? 'Creating...' : 'Create Tag'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);

TagInput.displayName = 'TagInput';

export default TagInput;
